/**
 * State.ts — central mutable simulation state. UI binds to this, passes read it.
 *
 * Stage 1 surface: wind, water level, sun parameters, rendering toggles.
 * Future stages add: boats[], time-of-day (kept fixed = warm afternoon), etc.
 */

export type CameraMode = "external" | "cockpit" | "free" | "flyby";

export type AnchorDeploymentState = "up" | "dropping" | "down" | "weighing";
export type AnchorHoldingState = "stowed" | "setting" | "holding" | "dragging";

/**
 * Render-quality preset. Picked at startup (changing it triggers a reload
 * because shadow-map and SSAO targets are baked at construction time):
 *  - low    : shadow 1024², SSAO off          (cheapest, ~+20 fps)
 *  - medium : shadow 2048², SSAO on           (current default)
 *  - high   : shadow 4096², SSAO on, intense  (best quality)
 */
export type RenderQuality = "low" | "medium" | "high";

export interface AppState {
  /** Wind direction, meteorological azimuth in DEGREES. UI-facing. */
  windDirectionDeg: number;
  /** Wind speed at 10 m height, m/s. */
  windSpeed: number;
  /** Choppiness factor for FFT horizontal displacement (0..2). */
  choppiness: number;
  /** Global water level in metres (world-space Y). */
  waterLevel: number;
  /** Sun azimuth in DEGREES (UI). 0 = north. */
  sunAzimuthDeg: number;
  /** Sun elevation above horizon in DEGREES (UI). */
  sunElevationDeg: number;
  /** Camera mode for the player boat. */
  cameraMode: CameraMode;
  /** Boat library id of the player-controlled boat. */
  playerBoatId: string;
  /** Terrain preset id (folder under public/terrain/). */
  terrainId: string;
  /** Show animated wind-streak particles. */
  showWindStreaks: boolean;
  /** Show occasional flying birds (single gulls or small V-flocks). */
  showBirds: boolean;
  /** Player anchor deployment state, driven by the crew anchor UI. */
  anchorState: AnchorDeploymentState;
  /** Length of anchor rode/chain currently paid out, metres. */
  anchorChainLength: number;
  /** Estimated depth below waterline at the drop point, metres. */
  anchorEstimatedDepth: number;
  /** World X/Z of the deployed seabed anchor; null while not deployed. */
  anchorWorldX: number | null;
  anchorWorldZ: number | null;
  /** Operational holding status derived by the simulation. */
  anchorHoldingState: AnchorHoldingState;
  /** Visual deployment progress for the boat GLB anchor part, 0=stowed, 1=deployed. */
  anchorDeployT: number;
  /** Whether the port-side fender group (GLB part "Fenders.Port") is shown. */
  fendersPortVisible: boolean;
  /** Whether the starboard-side fender group (GLB part "Fenders.Starboard"/"Fenders.Stern") is shown. */
  fendersStarboardVisible: boolean;
  /**
   * Per-position fender deployment, keyed by CREW_FENDERS keys
   * (`bow_port`, `bow_starboard`, `midship_port`, `midship_starboard`,
   * `stern_port`, `stern_starboard`). Drives the per-fender physics
   * pendulum visibility in {@link PlayerFenders}.
   */
  fenderDeployed: Record<string, boolean>;
  /**
   * Mooring assignments: which dock bollard (or buoy) each boat cleat is
   * attached to. `null` = line is loose / not in use. Keys are cleat names
   * from the boat anchor preset (e.g. "bow", "stern_port", "midship_starboard").
   * Values are anchor ids from the dock spec (e.g. "P1", "S1", "E1").
   */
  mooringTargets: Record<string, string | null>;
  /** Per-cleat rest length of the mooring rope (m). */
  mooringRestLengths: Record<string, number>;
  /**
   * Global wind multiplier in [0..1] derived from the active terrain's
   * `wind_exposure`. Applied to both wave growth (OceanSimulation) and
   * the aerodynamic force on boats so that sheltered bays feel calmer
   * even when the forecast wind speed is unchanged.
   */
  windExposure: number;
  /**
   * Hard cap on FFT wave amplitudeScale (see OceanSimulation). Smaller
   * basins clamp this lower so big waves can't build up.
   */
  maxWaveAmpScale: number;
  /** Body of water type for the active terrain. Drives water palette tint. */
  waterType: "lake" | "sea";
  /** Render-quality preset (low/medium/high). Changing it triggers reload. */
  renderQuality: RenderQuality;
  /** Diagnostics: overlay the player boat collision footprint on the water. */
  showCollisionFootprint: boolean;
  /** Diagnostics: draw the wireframe of every static collision AABB. */
  showStaticColliders: boolean;
}

export function defaultAppState(): AppState {
  return {
    windDirectionDeg: 270, // wind from west, blowing east
    windSpeed: 6, // moderate breeze
    choppiness: 0.7,
    waterLevel: 0,
    sunAzimuthDeg: 45, // SE
    sunElevationDeg: 50, // afternoon
    cameraMode: "external",
    playerBoatId: "antila_27",
    terrainId: "zielona_zatoka",
    showWindStreaks: false,
    showBirds: true,
    anchorState: "up",
    anchorChainLength: 0,
    anchorEstimatedDepth: 3,
    anchorWorldX: null,
    anchorWorldZ: null,
    anchorHoldingState: "stowed",
    anchorDeployT: 0,
    fendersPortVisible: false,
    fendersStarboardVisible: false,
    fenderDeployed: {
      bow_port: false,
      bow_starboard: false,
      midship_port: false,
      midship_starboard: false,
      stern_port: false,
      stern_starboard: false,
    },
    mooringTargets: {
      bow: null,
      stern_port: null,
      stern_starboard: null,
      midship_port: null,
      midship_starboard: null,
    },
    mooringRestLengths: {
      bow: 8,
      stern_port: 8,
      stern_starboard: 8,
      midship_port: 6,
      midship_starboard: 6,
    },
    windExposure: 1.0,
    maxWaveAmpScale: 2.0,
    waterType: "lake",
    renderQuality: "medium",
    showCollisionFootprint: false,
    showStaticColliders: false,
  };
}

/**
 * Shallow clone of an `AppState` that also duplicates the nested
 * mooring dictionaries so callers can mutate the snapshot without
 * leaking writes into the live sim state.
 *
 * Defined here (instead of the barrel) so app-layer modules can
 * import it without dragging `simulation-core/main.ts` (and the
 * entire renderer / CastOffProApp graph) into their chunk.
 */
export function cloneAppState(state: AppState): AppState {
  return {
    ...state,
    mooringTargets: { ...state.mooringTargets },
    mooringRestLengths: { ...state.mooringRestLengths },
  };
}
